<template>
  <Transition name="cookie-banner">
    <div
      v-if="visible"
      role="dialog"
      aria-modal="false"
      aria-label="Cookie settings"
      class="fixed inset-x-4 bottom-[calc(env(safe-area-inset-bottom)+16px)] z-90 tablet-h:inset-x-auto tablet-h:right-6 tablet-h:bottom-6 tablet-h:w-[410px]"
    >
      <div
        class="ck-card relative rounded-[20px] bg-white p-4 tablet-h:px-[26px] tablet-h:pt-[20px] tablet-h:pb-[26px]"
      >
        <button
          v-if="expanded"
          type="button"
          aria-label="Close cookie settings"
          class="absolute right-[13px] top-[13px] flex size-[22px] cursor-pointer items-center justify-center rounded-[6px] text-gray-300 transition hover:bg-gray-200/25 hover:text-gray-500"
          @click="onClose"
        >
          <CloseIcon class="size-[13px]" aria-hidden="true" />
        </button>

        <h2 class="text-[18px] font-medium leading-[22px] text-gray-800">
          Cookie Settings
        </h2>

        <template v-if="!expanded">
          <p class="mt-[10px] text-[14px] leading-[18px] text-gray-800">
            We use necessary cookies to operate Dacr. With your permission, we
            also use analytics and advertising technologies. See our
            <a
              class="ck-link"
              href="/legal/cookies"
              target="_blank"
              rel="noopener"
              >Cookie Policy</a
            >
            for details.
          </p>

          <div class="mt-[20px] flex flex-col gap-[10px]">
            <Button
              variant="stroke"
              size="md"
              class="w-full"
              @click="expanded = true"
            >
              Customize cookie settings
            </Button>
            <div class="grid grid-cols-2 gap-[8px]">
              <Button
                variant="stroke"
                size="md"
                class="w-full"
                @click="onDecline"
              >
                Decline
              </Button>
              <Button
                variant="primary"
                size="md"
                class="w-full"
                @click="onAccept"
              >
                Accept
              </Button>
            </div>
          </div>
        </template>

        <template v-else>
          <p class="mt-[10px] text-[14px] leading-[18px] text-gray-800">
            Dacr uses cookies and similar technologies. Necessary technologies
            keep the app working. With your permission, we and third-party
            providers also use analytics and advertising technologies. You can
            change your choices at any time. See our
            <a
              class="ck-link"
              href="/legal/cookies"
              target="_blank"
              rel="noopener"
              >Cookie Policy</a
            >
            for details.
          </p>

          <div
            v-if="gpc"
            role="alert"
            class="mt-[20px] flex items-start gap-[8px] rounded-[12px] bg-gray-800 px-[14px] py-[10px] text-white"
          >
            <InfoSmallIcon
              class="mt-[2px] size-[12px] shrink-0"
              aria-hidden="true"
            />
            <p class="text-[13px] font-medium leading-[17px]">
              Global Privacy Control enabled. Disable to modify cookies.
            </p>
          </div>

          <ul
            class="flex flex-col gap-[10px]"
            :class="gpc ? 'mt-[10px]' : 'mt-[20px]'"
          >
            <li
              class="ck-row flex items-center gap-[12px] rounded-[12px] bg-[#f9fafc] px-[12px] py-[8px]"
            >
              <div class="min-w-0 flex-1">
                <p class="text-[13px] font-medium leading-[18px] text-gray-800">
                  Necessary (Required)
                </p>
                <p class="mt-[2px] text-[13px] leading-[17px] text-gray-500">
                  Enables security, sign-in, and basic functionality.
                </p>
              </div>
              <Toggle
                :model-value="true"
                :size="isDesktopLayout ? 'md' : 'lg'"
                disabled
                aria-label="Necessary (Required)"
              />
            </li>

            <li
              v-for="row in optionalRows"
              :key="row.key"
              class="ck-row flex items-center gap-[12px] rounded-[12px] bg-[#f9fafc] px-[12px] py-[8px]"
            >
              <div class="min-w-0 flex-1">
                <p class="text-[13px] font-medium leading-[18px] text-gray-800">
                  {{ row.label }}
                </p>
                <p class="mt-[2px] text-[13px] leading-[17px] text-gray-500">
                  {{ row.description }}
                </p>
              </div>
              <Toggle
                v-model="categories[row.key]"
                :size="isDesktopLayout ? 'md' : 'lg'"
                :disabled="gpc"
                :aria-label="row.label"
              />
            </li>
          </ul>

          <Button
            variant="primary"
            size="md"
            class="mt-[20px] w-full"
            :disabled="gpc"
            @click="onSave"
          >
            Save preferences
          </Button>
        </template>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, reactive, ref } from 'vue'
import { Button, Toggle } from '@dacr/library'
import { CloseIcon, InfoSmallIcon } from '@dacr/icons'
import { subscribe, accept, decline, save } from '@dacr/consent'
import type { EffectiveState } from '@dacr/consent'

import { useDevice } from '@/shared/composables/useDevice'

type OptionalKey = 'analytics' | 'advertising'

const { isDesktopLayout } = useDevice()

const visible = ref(false)
const expanded = ref(false)
const gpc = ref(false)
const categories = reactive<Record<OptionalKey, boolean>>({
  analytics: false,
  advertising: false,
})

const optionalRows: { key: OptionalKey; label: string; description: string }[] =
  [
    {
      key: 'analytics',
      label: 'Analytics',
      description: 'Enables usage and interaction analytics.',
    },
    {
      key: 'advertising',
      label: 'Advertising',
      description: 'Enables ads measurement and personalization.',
    },
  ]

const render = (state: EffectiveState) => {
  gpc.value = state.gpc
  categories.analytics = state.granted.analytics
  categories.advertising = state.granted.advertising
  visible.value = state.showBanner
  if (!state.showBanner) expanded.value = false
}

const onClose = () => {
  visible.value = false
  expanded.value = false
}
const onAccept = () => accept()
const onDecline = () => decline()
const onSave = () => {
  save({ analytics: categories.analytics, advertising: categories.advertising })
  onClose()
}

let unsubscribe: (() => void) | null = null
onMounted(() => {
  unsubscribe = subscribe(render)
})
onUnmounted(() => unsubscribe?.())
</script>

<style scoped>
.ck-card {
  box-shadow:
    0 0 0 1px rgb(0 0 0 / 0.08),
    0 0.8px 2.925px rgb(0 0 0 / 0.02),
    0 2.025px 7.8px rgb(0 0 0 / 0.004),
    0 4px 18px rgb(0 0 0 / 0.04);
}

.ck-row {
  box-shadow: inset 0 0 0 1px #f0f1f3;
}

.ck-link {
  color: var(--color-green-700);
  font-weight: 500;
  transition: opacity 0.2s ease;
}

.ck-link:hover {
  opacity: 0.75;
}

.cookie-banner-enter-active,
.cookie-banner-leave-active {
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}

.cookie-banner-enter-from,
.cookie-banner-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

@media (prefers-reduced-motion: reduce) {
  .cookie-banner-enter-active,
  .cookie-banner-leave-active {
    transition: none;
  }
}
</style>
